import { motion, AnimatePresence } from 'framer-motion';
import { FiClock, FiX } from 'react-icons/fi';
import { PREMIUM_FEATURES_LOST } from '../../config/premium-features';
import { usePromptDialog } from '../../hooks/usePromptDialog';
import { useScrimClose } from '../../hooks/useScrimClose';

interface TrialCountdownModalProps {
  isOpen: boolean;
  daysRemaining: number;
  onDismiss: () => void;
  onUpgrade: () => void;
}

const TrialCountdownModal = ({
  isOpen,
  daysRemaining,
  onDismiss,
  onUpgrade,
}: TrialCountdownModalProps) => {
  const isUrgent = daysRemaining <= 1;
  // A press begun in the panel and let go over the scrim keeps the dialog
  // (hooks/useScrimClose); at every width, since the scrim always shows.
  const scrim = useScrimClose({ onClose: onDismiss });

  // The dialog contract (hooks/usePromptDialog): focus in, Tab kept inside,
  // Escape and Android Back dismiss (as Escape did), focus back to the opener.
  const { dialogRef } = usePromptDialog(isOpen, onDismiss);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-(--ee-z-dialog) flex items-center justify-center p-4 bg-(--ee-scrim)/50 outline-none"
          onMouseDownCapture={scrim.onMouseDownCapture}
          onMouseUpCapture={scrim.onMouseUpCapture}
          onClick={scrim.onClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="trial-countdown-title"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-(--ee-surface) rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-(--ee-tw-gray-200-line)">
              <h2
                id="trial-countdown-title"
                className="text-lg font-semibold text-(--ee-tw-gray-900)"
              >
                Free Trial Ending Soon
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={onDismiss}
                className="p-2 text-(--ee-tw-gray-500) hover:text-(--ee-tw-gray-700) hover:bg-(--ee-tw-gray-100-bg) rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Icon + countdown */}
              <div className="text-center">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                    isUrgent ? 'bg-(--ee-tw-red-100-bg)' : 'bg-(--ee-tw-amber-100-bg)'
                  }`}
                >
                  <FiClock
                    className={`text-2xl ${isUrgent ? 'text-(--ee-tw-red-600)' : 'text-(--ee-tw-amber-600)'}`}
                  />
                </div>
                <h3 className="text-xl font-bold text-(--ee-tw-gray-900) mb-1">
                  Your Free Trial Ends{' '}
                  {daysRemaining <= 0
                    ? 'Today'
                    : daysRemaining === 1
                      ? 'Tomorrow'
                      : `in ${daysRemaining} Days`}
                </h3>
                <p className="text-sm text-(--ee-tw-gray-600)">
                  Subscribe now to keep access to all premium features.
                </p>
              </div>

              {/* Features they'll lose */}
              <div
                className={`rounded-xl p-4 border ${
                  isUrgent
                    ? 'bg-(--ee-tw-red-50-bg) border-(--ee-tw-red-200-line)'
                    : 'bg-(--ee-tw-amber-50-bg) border-(--ee-tw-amber-200-line)'
                }`}
              >
                <h4
                  className={`text-sm font-medium mb-2 ${
                    isUrgent ? 'text-(--ee-tw-red-800)' : 'text-(--ee-tw-amber-800)'
                  }`}
                >
                  You&apos;ll lose access to:
                </h4>
                <ul className="space-y-1.5">
                  {PREMIUM_FEATURES_LOST.map((feature) => (
                    <li
                      key={feature}
                      className={`text-sm flex items-center gap-2 ${
                        isUrgent ? 'text-(--ee-tw-red-700)' : 'text-(--ee-tw-amber-700)'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          isUrgent ? 'bg-(--ee-tw-red-400-bg)' : 'bg-(--ee-tw-amber-400-bg)'
                        }`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={onUpgrade}
                  className="w-full py-3 px-4 bg-primary text-(--ee-on-accent) rounded-xl font-medium hover:bg-(--ee-tw-orange-600-bg) transition-colors"
                >
                  Upgrade Now
                </button>
                <button
                  onClick={onDismiss}
                  className="w-full py-3 px-4 border border-(--ee-tw-gray-300-line) rounded-xl font-medium text-(--ee-tw-gray-700) hover:bg-(--ee-tw-gray-50-bg) transition-colors text-sm"
                >
                  Remind Me Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default TrialCountdownModal;
